import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Repeat, PauseOctagon, Eye, MailCheck, ShieldAlert, PlayCircle, Users, TrendingUp, Clock,
} from 'lucide-react';

/**
 * Lifecycle campaigns control room — a status board, not a builder.
 * Three journeys, each with a state, a few numbers and a switch; the brake
 * sits above everything because that is what an admin needs at 11pm.
 */
const AdminCampaigns = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testFor, setTestFor] = useState(null); // {journey, step}
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [confirmJourney, setConfirmJourney] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/campaigns');
      setBoard(data);
    } catch (e) {
      toast.error('Could not load campaigns', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchJourney = async (journey, patch) => {
    try {
      const { data } = await api.patch(`/admin/campaigns/${journey}`, patch);
      setBoard(data);
      toast.success('Updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const togglePauseAll = async (paused) => {
    try {
      const { data } = await api.post('/admin/campaigns/pause-all', { paused });
      setBoard(data);
      toast.success(paused ? 'All campaigns paused — nothing will send' : 'Campaigns resumed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change pause state'); }
  };

  const openPreview = async (journey) => {
    try {
      const { data } = await api.get('/admin/campaigns/preview', { params: journey ? { journey } : {} });
      setPreview({ ...data, journey });
      setPreviewOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not build the preview'); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) return toast.error('Enter an address');
    try {
      await api.post(`/admin/campaigns/${testFor.journey}/test-send`, {
        step: testFor.step, email: testEmail.trim(),
      });
      toast.success(`Step ${testFor.step} sent to ${testEmail.trim()}`);
      setTestFor(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test send failed'); }
  };

  const confirmFirstRun = async () => {
    try {
      const { data } = await api.post(`/admin/campaigns/${confirmJourney}/confirm-first-run`);
      setBoard(data);
      setConfirmJourney(null);
      toast.success('First live run approved — this journey will send on the next daily pass');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not confirm'); }
  };

  const runNow = async (dryRun) => {
    try {
      const { data } = await api.post('/admin/campaigns/run-now', { dry_run: dryRun });
      toast.success(
        dryRun
          ? `Dry run: ${data.evaluated} would be emailed today`
          : `Pass complete — ${data.sent || 0} sent, ${data.held || 0} held, ${data.failed || 0} failed`,
        { description: (data.awaiting_first_run_confirmation || []).length
          ? `Awaiting first-run confirmation: ${data.awaiting_first_run_confirmation.join(', ')}` : undefined },
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Pass failed'); }
  };

  if (loading || !board) {
    return <div className='py-16 text-center text-muted-foreground' data-testid='campaigns-loading'>Loading campaigns…</div>;
  }

  const journeys = Object.values(board.journeys || {});

  return (
    <div className='space-y-5' data-testid='admin-campaigns-page'>
      <div>
        <h1 className='flex items-center gap-2 text-xl font-bold tracking-tight sm:text-2xl'>
          <Repeat className='h-5 w-5 text-primary' /> Lifecycle campaigns
        </h1>
        <p className='mt-1 text-sm text-muted-foreground'>
          Automatic nudges for users who never posted, went quiet, or let an ad lapse. Audiences are computed daily
          from platform activity — there is no list to maintain.
        </p>
      </div>

      {/* The brake */}
      <Card className={`flex flex-wrap items-center justify-between gap-3 p-4 ${board.global_paused ? 'border-[hsl(var(--danger))] bg-[hsl(var(--danger))]/5' : ''}`}>
        <div className='flex items-center gap-2.5'>
          <PauseOctagon className={`h-5 w-5 ${board.global_paused ? 'text-[hsl(var(--danger))]' : 'text-muted-foreground'}`} />
          <div>
            <div className='font-semibold'>{board.global_paused ? 'All campaigns are paused' : 'Campaigns are live'}</div>
            <div className='text-xs text-muted-foreground'>
              {board.global_paused
                ? 'The daily pass runs but sends nothing.'
                : `At most 1 email per user per ${board.caps.min_days_between_emails} days · max ${board.caps.max_emails_per_year} per year · dropped after ${board.caps.disengage_after} with no visit`}
            </div>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Button variant='outline' size='sm' onClick={() => openPreview(null)} data-testid='campaigns-preview-all'>
            <Eye className='mr-1.5 h-4 w-4' /> Preview today
          </Button>
          {isAdmin && (
            <>
              <Button variant='outline' size='sm' onClick={() => runNow(true)} data-testid='campaigns-dry-run'>
                <PlayCircle className='mr-1.5 h-4 w-4' /> Dry run
              </Button>
              <Button
                variant={board.global_paused ? 'default' : 'outline'}
                size='sm'
                onClick={() => togglePauseAll(!board.global_paused)}
                data-testid='campaigns-pause-all'
              >
                {board.global_paused ? 'Resume all' : 'Pause all campaigns'}
              </Button>
            </>
          )}
        </div>
      </Card>

      <div className='grid gap-3 sm:grid-cols-3'>
        <Card className='p-3' data-testid='campaigns-stat-optouts'>
          <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Opted out of reminders</div>
          <div className='text-2xl font-bold tabular-nums'>{board.opted_out_users}</div>
        </Card>
        <Card className='p-3' data-testid='campaigns-stat-dropped'>
          <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Dropped for disengagement</div>
          <div className='text-2xl font-bold tabular-nums'>{board.dropped_users}</div>
        </Card>
        <Card className='p-3' data-testid='campaigns-stat-dormancy'>
          <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Dormancy cut-off</div>
          <div className='text-2xl font-bold tabular-nums'>{board.caps.max_dormancy_days} days</div>
        </Card>
      </div>

      <div className='space-y-3'>
        {journeys.map((j) => (
          <Card key={j.key} className='p-4' data-testid={`campaign-journey-${j.key}`}>
            <div className='flex flex-wrap items-start justify-between gap-3'>
              <div className='min-w-0'>
                <div className='flex flex-wrap items-center gap-2'>
                  <span className='font-semibold'>{j.label}</span>
                  <Badge className={j.enabled && !board.global_paused ? 'bg-[hsl(var(--verified))]/10 text-[hsl(var(--verified))]' : 'bg-muted text-muted-foreground'} data-testid={`campaign-state-${j.key}`}>
                    {board.global_paused ? 'paused (global)' : j.enabled ? 'running' : 'off'}
                  </Badge>
                  {!j.first_run_confirmed && (
                    <Badge className='bg-amber-100 text-amber-800' data-testid={`campaign-awaiting-${j.key}`}>
                      first run not approved
                    </Badge>
                  )}
                </div>
                <div className='text-sm text-muted-foreground'>{j.description}</div>
                <div className='mt-0.5 text-xs text-muted-foreground'>Exits when: {j.exit}</div>
              </div>
              <div className='flex flex-wrap items-center gap-2'>
                <Button variant='outline' size='sm' onClick={() => openPreview(j.key)} data-testid={`campaign-preview-${j.key}`}>
                  <Eye className='mr-1.5 h-4 w-4' /> Preview
                </Button>
                {isAdmin && !j.first_run_confirmed && (
                  <Button size='sm' onClick={() => setConfirmJourney(j.key)} data-testid={`campaign-confirm-${j.key}`}>
                    <ShieldAlert className='mr-1.5 h-4 w-4' /> Approve first run
                  </Button>
                )}
                {isAdmin && (
                  <label className='flex items-center gap-2 text-xs'>
                    <Switch
                      checked={!!j.enabled}
                      onCheckedChange={(v) => patchJourney(j.key, { enabled: v })}
                      data-testid={`campaign-toggle-${j.key}`}
                    />
                    On
                  </label>
                )}
              </div>
            </div>

            {/* The numbers that matter lead with conversion, not volume */}
            <div className='mt-3 grid gap-2 sm:grid-cols-4'>
              <div className='rounded-lg border bg-secondary/30 p-2.5' data-testid={`campaign-waiting-${j.key}`}>
                <div className='flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground'>
                  <Users className='h-3 w-3' /> Waiting now
                </div>
                <div className='text-xl font-bold tabular-nums'>{j.waiting_total}</div>
              </div>
              <div className='rounded-lg border bg-secondary/30 p-2.5'>
                <div className='flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground'>
                  <Clock className='h-3 w-3' /> Emailed 7d / 30d
                </div>
                <div className='text-xl font-bold tabular-nums'>{j.sent_7d} / {j.sent_30d}</div>
              </div>
              <div className='rounded-lg border border-[hsl(var(--verified))]/30 bg-[hsl(var(--verified))]/5 p-2.5' data-testid={`campaign-converted-${j.key}`}>
                <div className='flex items-center gap-1 text-[11px] uppercase tracking-wider text-muted-foreground'>
                  <TrendingUp className='h-3 w-3' /> {j.conversion_label} (30d)
                </div>
                <div className='text-xl font-bold tabular-nums text-[hsl(var(--verified))]'>{j.converted_30d}</div>
              </div>
              <div className='rounded-lg border bg-secondary/30 p-2.5'>
                <div className='text-[11px] uppercase tracking-wider text-muted-foreground'>Held for review (30d)</div>
                <div className='text-xl font-bold tabular-nums'>{j.held_30d}</div>
              </div>
            </div>

            <div className='mt-3 overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr className='text-left text-[11px] uppercase tracking-wider text-muted-foreground'>
                    <th className='py-1.5 pr-3 font-medium'>Step</th>
                    <th className='py-1.5 pr-3 font-medium'>Timing</th>
                    <th className='py-1.5 pr-3 font-medium'>Waiting</th>
                    <th className='py-1.5 pr-3 font-medium'>Sent</th>
                    <th className='py-1.5 pr-3 font-medium'>Converted</th>
                    <th className='py-1.5 pr-3 font-medium'>On</th>
                    <th className='py-1.5 font-medium'>Test</th>
                  </tr>
                </thead>
                <tbody>
                  {j.steps.map((s) => (
                    <tr key={s.step} className='border-t' data-testid={`campaign-step-${j.key}-${s.step}`}>
                      <td className='py-2 pr-3 font-medium'>Step {s.step}</td>
                      <td className='py-2 pr-3 text-muted-foreground'>Day {s.day}</td>
                      <td className='py-2 pr-3 tabular-nums'>{s.waiting}</td>
                      <td className='py-2 pr-3 tabular-nums'>{s.sent_total}</td>
                      <td className='py-2 pr-3 tabular-nums'>{s.converted_total}</td>
                      <td className='py-2 pr-3'>
                        {isAdmin ? (
                          <Switch
                            checked={!!s.enabled}
                            onCheckedChange={(v) => patchJourney(j.key, { steps: { [String(s.step)]: v } })}
                            data-testid={`campaign-step-toggle-${j.key}-${s.step}`}
                          />
                        ) : (s.enabled ? 'on' : 'off')}
                      </td>
                      <td className='py-2'>
                        {isAdmin && (
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => { setTestFor({ journey: j.key, step: s.step }); setTestEmail(user?.email || ''); }}
                            data-testid={`campaign-test-${j.key}-${s.step}`}
                          >
                            <MailCheck className='h-4 w-4' />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>

      {/* Dry-run preview */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className='max-w-2xl' data-testid='campaigns-preview-dialog'>
          <DialogHeader>
            <DialogTitle>Today&apos;s recipients{preview?.journey ? ` — ${preview.journey}` : ''}</DialogTitle>
            <DialogDescription>
              Exactly who the next pass would reach, using the same rules the live pass uses. Nothing is sent from here.
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <div className='text-sm' data-testid='campaigns-preview-total'>
              <strong>{preview?.total ?? 0}</strong> recipient(s) ·{' '}
              {Object.entries(preview?.by_step || {}).map(([k, v]) => `${k} → ${v}`).join(' · ') || 'none due'}
              {preview?.truncated ? ` · showing the first ${preview.shown}` : ''}
            </div>
            <div className='max-h-72 overflow-y-auto rounded-lg border'>
              <table className='w-full text-sm'>
                <tbody>
                  {(preview?.recipients || []).map((r, i) => (
                    <tr key={`${r.email}-${i}`} className='border-b last:border-0'>
                      <td className='p-2 text-muted-foreground'>{r.journey} · step {r.step}</td>
                      <td className='p-2'>{r.name || '—'}</td>
                      <td className='p-2 text-muted-foreground'>{r.email}</td>
                      <td className='p-2 text-right tabular-nums text-muted-foreground'>{r.days}d</td>
                    </tr>
                  ))}
                  {!(preview?.recipients || []).length && (
                    <tr><td className='p-4 text-center text-muted-foreground'>Nobody is due today.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test send */}
      <Dialog open={!!testFor} onOpenChange={(o) => !o && setTestFor(null)}>
        <DialogContent data-testid='campaigns-test-dialog'>
          <DialogHeader>
            <DialogTitle>Test send — {testFor?.journey} step {testFor?.step}</DialogTitle>
            <DialogDescription>Sends this exact step to one address so you can read it on a phone.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Send to</Label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid='campaigns-test-email' />
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setTestFor(null)}>Cancel</Button>
            <Button onClick={sendTest} data-testid='campaigns-test-submit'>
              <MailCheck className='mr-1.5 h-4 w-4' /> Send test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* First live run confirmation */}
      <Dialog open={!!confirmJourney} onOpenChange={(o) => !o && setConfirmJourney(null)}>
        <DialogContent data-testid='campaigns-confirm-dialog'>
          <DialogHeader>
            <DialogTitle>Approve the first live run?</DialogTitle>
            <DialogDescription>
              Until this is approved, the daily pass works out who qualifies for <strong>{confirmJourney}</strong> but
              sends nothing. Approving lets it start emailing on the next pass, within the frequency caps.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setConfirmJourney(null)}>Not yet</Button>
            <Button onClick={confirmFirstRun} data-testid='campaigns-confirm-submit'>Approve first run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCampaigns;
